import { Component, Input, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { localUrl } from 'app/common/url-resolver.service';
import { MessageHandlerService } from 'app/common/common/message-handler/message-handler.service';
import {
  AttachmentsService,
  BAttachment,
  BHistory,
  BInquiry,
  BInteraction,
  BMedicalDocument,
  SingleInteractionService,
} from 'app/modules/data-model/data-model.module';
import { InteractionsService } from 'app/modules/data-model/interaction/interactions.service';
import { SelectInquiryComponent } from './select-inquiry/select-inquiry.component';
import { HISTORY_DIALOG_MODES } from 'app/common/types';
import { HistoryFilters } from '../detail-history-filters';
import { NgbdModalUser } from 'app/common/common/modal/modal-user/modal-user.component';
import { Helpers } from '@mi-tool/utils/helpers';
import { AuthService } from 'app/auth/auth.service';
import { PERM_PROCESS_ENQUIRIES } from 'app/common/common/constants/permissions';
import { GenericDialogService } from 'app/common/common/generic-dialog/generic-dialog.service';
import { EHistory } from './enchanted-history';

@Component({
  selector: 'app-detail-history-interaction',
  templateUrl: './detail-history-interaction.component.html',
  styleUrls: ['./detail-history-interaction.component.scss'],
})
export class DetailHistoryInteractionComponent implements OnDestroy {
  visibleHistory: EHistory[] = [];
  @Input() mode?: HISTORY_DIALOG_MODES = 'history';
  @Input() loggedInUserIsSysAdmin: boolean;
  disabled: boolean = true;

  private allHistory: EHistory[] = [];
  private historyFilters: HistoryFilters.HistoryFilter[];
  private _interaction: BInteraction;
  private _inquiry: BInquiry;
  private subs = new Subscription();

  constructor(
    private attachmentsService: AttachmentsService,
    private interactionsService: InteractionsService,
    private singleInteractionService: SingleInteractionService,
    private messageService: MessageHandlerService,
    private helpers: Helpers,
    private dialogService: GenericDialogService,
    authService: AuthService
  ) {
    this.subs.add(
      authService.permissions.subscribe((handler) => {
        this.disabled = !handler.has(PERM_PROCESS_ENQUIRIES);
      })
    );
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  @Input()
  set interaction(interaction: BInteraction) {
    if (interaction && (!this._interaction || interaction.pk() !== this._interaction.pk())) {
      this._interaction = interaction;
    }
  }

  get interaction(): BInteraction {
    return this._interaction;
  }

  @Input()
  set inquiry(inquiry: BInquiry) {
    this._inquiry = inquiry;
  }

  @Input()
  set interactionFilter(historyFilterValues: string[]) {
    this.historyFilters = HistoryFilters.resolveFilters(historyFilterValues);
    this.refreshFilter();
  }

  @Input()
  set history(val: InteractionsService.History) {
    const history = val.history;
    history.push(BHistory.createEntry(this.interaction));
    this.allHistory = EHistory.from(history, val.inquirers, this.helpers, this._interaction.pk());
    this.refreshFilter();
  }

  resolveInteractionUrl(interactionId: number): string {
    return localUrl('detail', interactionId);
  }

  previewUser(userId: number): void {
    this.dialogService.open(NgbdModalUser, { data: { userId: userId } });
  }

  previewInquirer(inquirerId: number): void {
    this.dialogService.open(NgbdModalUser, { data: { inquirerId: inquirerId } });
  }

  copyTextToAnswer(internalCom: BHistory): void {
    this.selectInquiry((inquiry) => {
      const text = this.stripBodyTags(inquiry.answer.text) + this.stripBodyTags(internalCom.text);
      this.subs.add(
        this.interactionsService
          .updateText(this._interaction.isNew(), inquiry.answer.pk(), text)
          .subscribe({
            next: () => {
              this.singleInteractionService.refreshInteraction(this._interaction.id);
            },
            error: (error) => {
              this.messageService.httpError('Copy Text to Answer', error);
            },
          })
      );
    });
  }

  copyToAnswer(attachment: BAttachment | BMedicalDocument): void {
    this.selectInquiry((inquiry) => {
      this.subs.add(
        this.attachmentsService.copyToAnswer(attachment.pk(), inquiry.answer.pk()).subscribe({
          next: () => {
            this.singleInteractionService.refreshInteraction(this._interaction.id);
          },
          error: (error) => {
            this.messageService.httpError('Copy Attachment to Answer', error);
          },
        })
      );
    });
  }

  private selectInquiry(callback: (inquiry: BInquiry) => void): void {
    if (this._interaction.inquiries.length > 1) {
      const modalRef = this.dialogService.open(SelectInquiryComponent);
      modalRef.componentInstance.interaction = this._interaction;
      modalRef.afterClosed().subscribe((inquiry) => {
        if (inquiry) {
          callback(inquiry);
        }
      });
    } else {
      callback(this._interaction.inquiries[0]);
    }
  }

  private refreshFilter(): void {
    const filters = this.historyFilters;
    this.visibleHistory = this.allHistory
      .filter((h) => !this._inquiry || !h.inquiryId || this._inquiry.id === h.inquiryId)
      .filter((h) => HistoryFilters.includes(filters, h));

    // Do not show merge as thank you message in internal communication dialog.
    if (this.mode === 'internal_comm') {
      this.visibleHistory = this.visibleHistory.filter(
        (history) => !(history.category === 'other' && history.historyDescription)
      );
    }
  }

  private stripBodyTags(html: string): string {
    if (!html) {
      html = '';
    } else if (html.includes('<body>')) {
      html = html.substring(html.indexOf('<body>') + 6, html.indexOf('</body>'));
    }
    return html;
  }
}
